/**
 * 游戏段位相关 Store
 * 负责段位数据的加载、管理和缓存
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCacheStore } from '../cache'
import { cacheKeys } from '../config'
import { ranksApi } from '@/https/api/ranks'
import type { GameRank, GameRankCreateRequest, GameRankUpdateRequest } from '@/https/types/ranks'

export const useRanksStore = defineStore('ranks', () => {
    const cacheStore = useCacheStore()

    // ==================== 状态管理 ====================

    // 段位列表
    const rankList = ref<GameRank[]>([])

    // 加载状态
    const rankListLoading = ref(false)

    // 操作加载状态
    const rankOperationLoading = ref(false)

    // 错误信息
    const rankListError = ref<string | null>(null)

    // ==================== 计算属性 ====================

    // 是否有段位数据
    const hasRankList = computed(() => rankList.value.length > 0)

    // 段位总数
    const rankCount = computed(() => rankList.value.length)

    // 按排序权重排序的段位列表
    const sortedRankList = computed(() => {
        return [...rankList.value].sort((a, b) => {
            // 排序权重小的排在前面，没有权重的排在后面
            const orderA = a.rank_order ?? 999999
            const orderB = b.rank_order ?? 999999
            return orderA - orderB
        })
    })

    // 段位选项（用于下拉选择）
    const rankOptions = computed(() => {
        return sortedRankList.value.map(rank => ({
            label: rank.rank_name,
            value: rank.id
        }))
    })

    // ==================== 数据加载 ====================

    /**
     * 加载所有段位数据
     * @param forceRefresh 是否强制刷新
     */
    const loadRankList = async (forceRefresh = false): Promise<GameRank[]> => {
        try {
            rankListLoading.value = true
            rankListError.value = null

            const response = await cacheStore.smartCache(
                cacheKeys.config.ranks,
                () => {
                    return ranksApi.getAllRanks()
                },
                forceRefresh
            )

            // 处理分页响应格式：提取 items 字段
            let ranksArray: GameRank[] = []
            if (Array.isArray(response)) {
                // 如果直接是数组（未分页）
                ranksArray = response
            } else if (response && typeof response === 'object' && 'items' in response) {
                // 如果是分页格式，提取 items
                ranksArray = Array.isArray(response.items) ? response.items : []
            }

            rankList.value = ranksArray
            return rankList.value
        } catch (error) {
            console.error('❌ [RankStore] 加载段位数据失败:', error)
            rankListError.value = error instanceof Error ? error.message : '加载段位数据失败'
            return []
        } finally {
            rankListLoading.value = false
        }
    }

    const setRankList = (items: GameRank[]) => {
        rankList.value = Array.isArray(items) ? items : []
        rankListError.value = null
    }

    /**
     * 刷新段位数据
     */
    const refreshRankList = async (): Promise<GameRank[]> => {
        return await loadRankList(true)
    }

    // ==================== 数据操作（管理员功能） ====================

    /**
     * 创建段位
     * @param rankData 段位数据
     */
    const createRank = async (rankData: GameRankCreateRequest): Promise<boolean> => {
        try {
            rankOperationLoading.value = true
            rankListError.value = null

            await ranksApi.createRank(rankData)

            // 重新加载段位列表
            await loadRankList(true)

            return true
        } catch (error) {
            console.error('创建段位失败:', error)
            rankListError.value = error instanceof Error ? error.message : '创建段位失败'
            return false
        } finally {
            rankOperationLoading.value = false
        }
    }

    /**
     * 更新段位
     * @param id 段位ID
     * @param rankData 段位数据
     */
    const updateRank = async (id: number, rankData: GameRankUpdateRequest): Promise<boolean> => {
        try {
            rankOperationLoading.value = true
            rankListError.value = null

            await ranksApi.updateRank(id, rankData)

            // 重新加载段位列表
            await loadRankList(true)

            return true
        } catch (error) {
            console.error('更新段位失败:', error)
            rankListError.value = error instanceof Error ? error.message : '更新段位失败'
            return false
        } finally {
            rankOperationLoading.value = false
        }
    }

    /**
     * 删除段位
     * @param id 段位ID
     */
    const deleteRank = async (id: number): Promise<boolean> => {
        try {
            rankOperationLoading.value = true
            rankListError.value = null

            await ranksApi.deleteRank(id)

            // 重新加载段位列表
            await loadRankList(true)

            return true
        } catch (error) {
            console.error('删除段位失败:', error)
            rankListError.value = error instanceof Error ? error.message : '删除段位失败'
            return false
        } finally {
            rankOperationLoading.value = false
        }
    }

    // ==================== 工具方法 ====================

    /**
     * 根据ID查找段位
     * @param id 段位ID
     */
    const findRankById = (id: number): GameRank | null => {
        return rankList.value.find(rank => rank.id === id) || null
    }

    /**
     * 根据名称查找段位
     * @param name 段位名称
     */
    const findRankByName = (name: string): GameRank | null => {
        return rankList.value.find(rank => rank.rank_name === name) || null
    }

    /**
     * 清除错误信息
     */
    const clearRankListError = (): void => {
        rankListError.value = null
    }

    // ==================== 初始化 ====================

    /**
     * 初始化段位数据
     */
    const initRanks = async (): Promise<void> => {
        try {
            await loadRankList()
        } catch (error) {
            console.error('初始化段位数据失败:', error)
        }
    }

    // ==================== 导出 ====================

    return {
        // 状态 - 直接返回ref，保持响应式
        rankList,
        rankListLoading,
        rankOperationLoading,
        rankListError,

        // 计算属性
        hasRankList,
        rankCount,
        sortedRankList,
        rankOptions,

        // 数据加载
        loadRankList,
        setRankList,
        refreshRankList,

        // 数据操作
        createRank,
        updateRank,
        deleteRank,

        // 工具方法
        findRankById,
        findRankByName,
        clearRankListError,

        // 初始化
        initRanks
    }
})
